interface IMergeCell {
  id: string;
  mergedInto?: string;
  colSpan?: number;
  rowSpan?: number;
}

interface IRectBounds {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

/**
 * Expand a rectangular grid selection so it fully contains every merged cell it
 * touches. Repeats until stable (one merge can pull in another); never shrinks.
 */
export const expandRectToMergeBounds = (
  tableData: IMergeCell[],
  cols: number,
  bounds: IRectBounds,
): IRectBounds => {
  let { minRow, maxRow, minCol, maxCol } = bounds;

  const byId = new Map(tableData.map((c) => [c.id, c]));
  const indexById = new Map(tableData.map((cell, index) => [cell.id, index]));

  let changed = true;
  while (changed) {
    changed = false;
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const cell = tableData[r * cols + c];
        if (!cell) continue;
        const anchor = cell.mergedInto ? byId.get(cell.mergedInto) : cell;
        if (!anchor) continue;
        const anchorIdx = indexById.get(anchor.id);
        if (anchorIdx === undefined) continue;
        const aRow = Math.floor(anchorIdx / cols);
        const aCol = anchorIdx % cols;
        const aRowEnd = aRow + (anchor.rowSpan || 1) - 1;
        const aColEnd = aCol + (anchor.colSpan || 1) - 1;
        if (aRow < minRow) {
          minRow = aRow;
          changed = true;
        }
        if (aCol < minCol) {
          minCol = aCol;
          changed = true;
        }
        if (aRowEnd > maxRow) {
          maxRow = aRowEnd;
          changed = true;
        }
        if (aColEnd > maxCol) {
          maxCol = aColEnd;
          changed = true;
        }
      }
    }
  }

  return { minRow, maxRow, minCol, maxCol };
};
